(() => {
  if (window.SbPremium) return;

  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const played = new WeakSet();
  const observed = new Set();
  const animations = new Map();
  const carousels = new Map();
  const artwork = '.sb-category-hub__tile, .sb-format-nav, .sb-home__world, .sb-home__promo, [data-sb-motion-hero], [data-sb-motion-intro]';
  const selector = `[data-sb-reveal], .sb-template-page .rte, .shopify-policy__container, ${artwork}`;
  let observer;

  const animate = (element, delay) => {
    if (preference.matches || played.has(element)) return;
    played.add(element);
    element.style.setProperty('--sb-motion-delay', `${delay}ms`);
    element.classList.add('sb-motion-ready');
    // Artwork has its own choreography; labels and purchase controls stay still.
    if (element.matches(artwork) || !element.animate) return;
    const animation = element.animate(
      [{ opacity: .85, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }],
      { duration: 480, delay, easing: 'cubic-bezier(.22,1,.36,1)' }
    );
    animations.set(element, animation);
    const release = () => animations.delete(element);
    animation.addEventListener('finish', release, { once: true });
    animation.addEventListener('cancel', release, { once: true });
  };

  const getObserver = () => {
    if (observer || !('IntersectionObserver' in window)) return observer;
    observer = new IntersectionObserver((entries) => {
      let order = 0;
      entries.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);
      entries.forEach((entry) => {
        const element = entry.target;
        element.classList.toggle('sb-motion-in-view', entry.isIntersecting);
        if (!entry.isIntersecting) {
          animations.get(element)?.cancel();
          return;
        }
        if (!document.hidden) animate(element, Math.min(order++ * 65, 195));
        if (!element.matches(artwork) && played.has(element)) {
          observer.unobserve(element);
          observed.delete(element);
        }
      });
    }, { threshold: .12 });
    return observer;
  };

  const initCarousel = (element) => {
    if (carousels.has(element) || !('MutationObserver' in window)) return;
    // Preserve the existing carousel, ARIA, swipe, timer and crossfade.
    // Only later selections animate the photograph; the first LCP image is static.
    const changes = new MutationObserver((records) => {
      records.forEach(({ target, oldValue }) => {
        if (!target.matches('[data-sb-slide]')) return;
        const selected = target.getAttribute('aria-hidden') === 'false';
        if (!selected) target.classList.remove('sb-motion-slide-enter');
        else if (oldValue === 'true' && !preference.matches && !document.hidden && element.classList.contains('sb-motion-in-view')) {
          target.classList.add('sb-motion-slide-enter');
        }
      });
    });
    changes.observe(element, { subtree: true, attributes: true, attributeFilter: ['aria-hidden'], attributeOldValue: true });
    carousels.set(element, changes);
  };

  const init = (root = document) => {
    if (!document.body.classList.contains('sb-premium')) return;
    root.querySelectorAll('[data-sb-motion-hero]').forEach(initCarousel);
    if (preference.matches) return;
    const targetObserver = getObserver();
    if (!targetObserver) return;
    const elements = [...root.querySelectorAll(selector)];
    if (root instanceof Element && root.matches(selector)) elements.unshift(root);
    elements.forEach((element) => {
      if (observed.has(element) || element.classList.contains('scroll-trigger')) return;
      observed.add(element);
      targetObserver.observe(element);
    });
  };

  const unload = (root) => {
    observed.forEach((element) => {
      if (root.contains(element)) {
        observer?.unobserve(element);
        observed.delete(element);
      }
    });
    animations.forEach((animation, element) => {
      if (root.contains(element)) animation.cancel();
    });
    carousels.forEach((changes, element) => {
      if (root.contains(element)) {
        changes.disconnect();
        carousels.delete(element);
      }
    });
  };

  const visibility = () => {
    document.body.classList.toggle('sb-motion-paused', document.hidden);
    animations.forEach((animation) => document.hidden ? animation.pause() : animation.play());
    if (!document.hidden && !preference.matches) {
      observed.forEach((element) => {
        if (element.classList.contains('sb-motion-in-view')) animate(element, 0);
      });
    }
  };
  preference.addEventListener('change', () => {
    if (preference.matches) {
      observer?.disconnect();
      observed.forEach((element) => element.classList.remove('sb-motion-in-view'));
      observed.clear();
      animations.forEach((animation) => animation.cancel());
      carousels.forEach((_, element) => element.querySelectorAll('.sb-motion-slide-enter').forEach(slide => slide.classList.remove('sb-motion-slide-enter')));
    } else init();
  });
  document.addEventListener('visibilitychange', visibility);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  window.SbPremium = { init };
  const start = () => { visibility(); init(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
